import React, { FC, useEffect, useState } from "react";
import { LIBRARY_DISBURSEMENTS_QUERY } from "../../../../graphql/query";
import { TaxLabelStatus } from "../../../../graphql/__generated_types__/globalTypes";
import { useMutation, useQuery } from "@apollo/client";
import {
  UpdateLibraryDisbursements,
  UpdateLibraryDisbursementsVariables,
} from "@graphql-types/UpdateLibraryDisbursements";
import {
  CreateLibraryDisbursement,
  CreateLibraryDisbursementVariables,
} from "@graphql-types/CreateLibraryDisbursement";
import {
  CREATE_LIBRARY_DISBURSEMENT,
  DELETE_LIBRARY_DISBURSEMENTS,
  UPDATE_LIBRARY_DISBURSEMENTS,
} from "../../../../graphql/mutation";
import { Button, Input, Select, Table, InputNumber, Form, Checkbox, Tooltip } from "antd";
import { InfoCircleOutlined, PlusOutlined, SaveOutlined } from "@ant-design/icons";
import { Icons, LibraryDisbursement } from "@design-system";
import styles from "./disbursements-manager.css";
import clsx from "clsx";
import {
  DeleteLibraryDisbursements,
  DeleteLibraryDisbursementsVariables,
} from "@graphql-types/DeleteLibraryDisbursements";
import { useSettings } from "~/hooks";
import { useDebouncedCallback } from "use-debounce";
import { compareAsc } from "date-fns";
import TextArea from "antd/lib/input/TextArea";

const {
  ["disbursement-form-item"]: disFormItem,
  ["disbursement-form-item-vat-status"]: disFormItemVatStatus,
  ["disbursement-form-top-field"]: disFormTopField,
  ["disbursement-form-top-field-right"]: disFormTopFieldRight,
  ["disbursement-form-bottom-button"]: disFormBottomButton,
  ["disbursement-form-bottom-button-right"]: disFormBottomButtonRight,
} = styles as any;

type LibDisCreate = {
  vatStatus: TaxLabelStatus;
  amount: number;
  name: string;
};

type LibDisUpdate = Partial<LibDisCreate> & {
  id: number;
};

export const DisbursementsManager = () => {
  const { loading, data } = useQuery(LIBRARY_DISBURSEMENTS_QUERY, {
    variables: {
      filter: {},
    } as any,
  });

  const libraryDisbursements = data?.libraryDisbursements || [];

  const [updateLibraryDisbursements] = useMutation<
    UpdateLibraryDisbursements,
    UpdateLibraryDisbursementsVariables
  >(UPDATE_LIBRARY_DISBURSEMENTS, { refetchQueries: [LIBRARY_DISBURSEMENTS_QUERY] });

  const [createLibraryDisbursement] = useMutation<
    CreateLibraryDisbursement,
    CreateLibraryDisbursementVariables
  >(CREATE_LIBRARY_DISBURSEMENT, { refetchQueries: [LIBRARY_DISBURSEMENTS_QUERY] });

  const [deleteLibraryDisbursements] = useMutation<
    DeleteLibraryDisbursements,
    DeleteLibraryDisbursementsVariables
  >(DELETE_LIBRARY_DISBURSEMENTS, { refetchQueries: [LIBRARY_DISBURSEMENTS_QUERY] });

  const onSave = (data: LibDisUpdate[]) => {
    updateLibraryDisbursements({
      variables: {
        data,
      },
    });
  };

  const onAddNew = (data: LibDisCreate) => {
    createLibraryDisbursement({ variables: { data } });
  };

  const onDelete = (disbursementIds: number[]) => {
    deleteLibraryDisbursements({ variables: { data: disbursementIds } });
  };

  return (
    <>
      <DisbursementsTable
        onSave={onSave}
        onAddNew={onAddNew}
        onDelete={onDelete}
        loading={loading}
        libraryDisbursements={libraryDisbursements}
      />
    </>
  );
};

const DisbursementsTable = ({
  loading: dataLoading,
  onSave,
  onAddNew,
  onDelete,
  libraryDisbursements,
}: {
  loading: boolean;
  onSave: (updates: LibDisUpdate[]) => void;
  onAddNew: (create: LibDisCreate) => void;
  onDelete: (ids: number[]) => void;
  libraryDisbursements: LibraryDisbursement[];
}) => {
  const [form] = Form.useForm();

  const { settings } = useSettings();

  const currencySymbol = settings?.currency.symbol ?? "";

  const [changedLibraryDisbursements, setChangedLibraryDisbursements] = useState<LibDisUpdate[]>(
    []
  );

  const [toDeleteLibraryDisbursements, setToDeleteLibraryDisbursements] = useState<number[]>([]);

  const onCancel = () => {
    setChangedLibraryDisbursements([]);
    form.resetFields();
  };

  const addNew = () => {
    onAddNew({
      vatStatus: TaxLabelStatus.Inclusive,
      amount: 0,
      name: "",
    });
  };

  const deleteDisbursements = () => {
    onDelete(toDeleteLibraryDisbursements);
    setToDeleteLibraryDisbursements([]);
  };

  const [formLoading, setFormLoading] = useState<boolean>(false);

  const loading = formLoading || dataLoading;

  const doOnFinish = () => {
    try {
      setFormLoading(true);
      onSave(changedLibraryDisbursements);
      setChangedLibraryDisbursements([]);
    } finally {
      setFormLoading(false);
    }
  };

  useEffect(() => {
    form.setFieldsValue(libraryDisbursements);
  }, [form, libraryDisbursements]);

  const dBhandleAmountChange = useDebouncedCallback(
    // function
    (changedFields) => {
      const { name: changedFieldNameRaw, value: changedFieldValue } = changedFields[0];
      const changedFieldName = changedFieldNameRaw as [number, keyof LibDisUpdate];
      const disbursementId = libraryDisbursementIdByIndex(
        changedFieldName[0],
        libraryDisbursements
      );
      const disbursementUpdateIndex = changedLibraryDisbursements.findIndex(
        (x) => x.id === disbursementId
      );
      let disbursementUpdate: LibDisUpdate;
      if (disbursementUpdateIndex >= 0) {
        disbursementUpdate = changedLibraryDisbursements[disbursementUpdateIndex];
        (disbursementUpdate as any)[changedFieldName[1]] = changedFieldValue;
      } else {
        disbursementUpdate = {
          id: disbursementId,
          [changedFieldName[1]]: changedFieldValue,
        };
      }
      setChangedLibraryDisbursements((curr) => [...curr, disbursementUpdate]);
    },
    // delay in ms
    600
  );

  return (
    <>
      <Form
        initialValues={libraryDisbursements}
        form={form}
        labelAlign="left"
        labelCol={{ span: 7 }}
        wrapperCol={{ span: 17, offset: 1 }}
        onFinish={doOnFinish}
        onFieldsChange={dBhandleAmountChange}
      >
        <Form.Item noStyle>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => addNew()}
            className={clsx(disFormTopField)}
          >
            Add New
          </Button>
          <Button
            disabled={loading || toDeleteLibraryDisbursements.length === 0}
            icon={Icons.Delete}
            onClick={() => deleteDisbursements()}
            className={clsx(disFormTopField, disFormTopFieldRight)}
          >
            Delete
          </Button>
        </Form.Item>
        <Table
          pagination={false}
          columns={[
            {
              title: "Name",
              dataIndex: "name",
              key: "name",
              render: (_, __, index) => {
                const fieldTouched = isFieldTouched(
                  changedLibraryDisbursements,
                  libraryDisbursements,
                  index,
                  "name"
                );
                return (
                  <Form.Item
                    className={clsx(disFormItem)}
                    name={[index, "name"]}
                    rules={[
                      {
                        required: true,
                        message: "Name",
                      },
                    ]}
                  >
                    <TextArea
                      status={fieldTouched ? "warning" : undefined}
                      disabled={loading}
                      placeholder="Name"
                    />
                  </Form.Item>
                );
              },
            },
            {
              title: (
                <>
                  <span>VAT Status</span>
                  <VatStatusTooltip />
                </>
              ),
              dataIndex: "vatStatus",
              key: "vatStatus",
              render: (_, __, index) => {
                const fieldTouched = isFieldTouched(
                  changedLibraryDisbursements,
                  libraryDisbursements,
                  index,
                  "vatStatus"
                );
                return (
                  <Form.Item
                    className={clsx(disFormItem, disFormItemVatStatus)}
                    name={[index, "vatStatus"]}
                    rules={[
                      {
                        required: true,
                        message: "VAT Status",
                      },
                    ]}
                  >
                    <Select
                      status={fieldTouched ? "warning" : undefined}
                      defaultValue={TaxLabelStatus.Inclusive}
                      disabled={loading}
                      options={[
                        { value: TaxLabelStatus.Inclusive, label: "Inclusive" },
                        { value: TaxLabelStatus.Exempt, label: "Exempt" },
                        { value: TaxLabelStatus.Exclusive, label: "Exclusive" },
                      ]}
                    />
                  </Form.Item>
                );
              },
            },
            {
              title: "Amount",
              dataIndex: "amount",
              key: "amount",
              render: (_, __, index) => {
                const fieldTouched = isFieldTouched(
                  changedLibraryDisbursements,
                  libraryDisbursements,
                  index,
                  "amount"
                );
                return (
                  <Form.Item
                    className={clsx(disFormItem)}
                    name={[index, "amount"]}
                    rules={[
                      {
                        required: true,
                        message: "Amount",
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "150px" }}
                      status={fieldTouched ? "warning" : undefined}
                      min={0}
                      max={9999999}
                      prefix={currencySymbol}
                      step={0.01}
                    />
                  </Form.Item>
                );
              },
            },
            {
              title: "Last updated",
              dataIndex: "updatedAt",
              key: "updatedAt",
              render: (_, row) => {
                return new Date(row.updatedAt).toLocaleString();
              },
              sorter: (a, b) => compareAsc(new Date(a.updatedAt), new Date(b.updatedAt)),
              width: "15rem",
            },
            {
              title: "Created by",
              dataIndex: ["createdBy", "displayName"],
              key: "createdBy",
              width: "15rem",
            },
            {
              title: Icons.Delete,
              dataIndex: "delete",
              key: "delete",
              render: (_, __, index) => {
                const disbursementId = libraryDisbursementIdByIndex(index, libraryDisbursements);
                return (
                  <Checkbox
                    disabled={loading}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      const disbursementIdIndex = toDeleteLibraryDisbursements.findIndex(
                        (d) => d === disbursementId
                      );
                      if (false === checked && disbursementIdIndex >= 0) {
                        setToDeleteLibraryDisbursements([
                          ...toDeleteLibraryDisbursements.slice(0, disbursementIdIndex),
                          ...toDeleteLibraryDisbursements.slice(disbursementIdIndex + 1),
                        ]);
                      } else if (disbursementIdIndex === -1) {
                        setToDeleteLibraryDisbursements([
                          ...toDeleteLibraryDisbursements,
                          disbursementId,
                        ]);
                      }
                    }}
                    checked={toDeleteLibraryDisbursements.includes(disbursementId)}
                  />
                );
              },
            },
          ]}
          dataSource={libraryDisbursements}
          loading={loading}
          rowKey="id"
          size="small"
        />
        <Form.Item shouldUpdate noStyle>
          {() => {
            return (
              <>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  disabled={loading || changedLibraryDisbursements.length === 0}
                  className={disFormBottomButton}
                >
                  Save
                </Button>
                <Button
                  onClick={() => onCancel()}
                  className={clsx(disFormBottomButton, disFormBottomButtonRight)}
                >
                  Cancel
                </Button>
              </>
            );
          }}
        </Form.Item>
      </Form>
    </>
  );
};

const libraryDisbursementIdByIndex = (
  index: number,
  libraryDisbrsements: LibraryDisbursement[]
) => {
  return libraryDisbrsements[index].id;
};

const isFieldTouched = (
  updates: LibDisUpdate[],
  libraryDisbursements: LibraryDisbursement[],
  index: number,
  field: keyof LibDisUpdate
) => {
  const touchedField = updates.find(
    (x) =>
      x.id === libraryDisbursementIdByIndex(index, libraryDisbursements) && x[field] !== undefined
  );
  return touchedField ? true : false;
};

const VatStatusTooltip: FC = ({ children }) => (
  <Tooltip
    title={() => {
      return (
        <>
          <p>
            <strong>Inclusive:</strong> The amount includes VAT in its total.
          </p>
          <p>
            <strong>Exclusive:</strong> The amount does not include VAT. VAT will be added on the
            proposal.
          </p>
          <p>
            <strong>Exempt:</strong> The amount does not include VAT. VAT will not be added.
          </p>
        </>
      );
    }}
    placement="top"
  >
    {children}
    <InfoCircleOutlined style={{ marginLeft: 5 }} />
  </Tooltip>
);
